/* eslint-disable no-unused-vars */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useLocation, useNavigation } from 'react-router-dom';
import { Col, Layout, Modal, Row, Typography } from 'antd';
import queryString from 'query-string';

import PdButton from 'components/pd-button';
import { InspectionCreateEdit } from './create-edit';
import { TableList } from 'components/table-list';

import { ProfileContext } from 'contexts/profile';
import ReviewInspectionDetail from 'services/reviewInspection';

import { convertNumberToRupiahCurrency, errHandling } from 'utils';
import { INSPECTION_STATUS } from './constant';
import './style.scss';

const { Content } = Layout;
const { Title } = Typography;

export function CheckingInspectionDetail() {
  const location = useLocation();
  const { fetchProfile } = useContext(ProfileContext);

  const [loadingButton, setLoadingButton] = useState(false);
  const [loadStatus, setLoadStatus] = useState(true);
  const [modalContent, setModalContent] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [inspectionDetail, setInspectionDetail] = useState([]);
  const [totalDepositAndDamage, setTotalDepositAndDamage] = useState({
    total_deposit: 0,
    damage_penalty: 0,
  });

  const navigate = useNavigation();

  const {
    total_deposit: totalDeposit,
    damage_penalty: damagePenalty,
    late_penalty: latePenalty,
  } = totalDepositAndDamage;

  const parsed = queryString.parse(location.search);

  const { id: orderId, status: pageStatus } = parsed;
  const { title: pageTitle = 'Inspection Detail' } = inspectionDetail;

  const returnToCustomerAmount = [+damagePenalty, +latePenalty].reduce(
    (acc, curr) => acc - curr,
    +totalDeposit
  );

  const buttonAction = (row) => (
    <Row className="button-table">
      <Col lg={24}>
        <PdButton
          className="height-32"
          full
          onClick={handleOpenModal.bind(this, row)}
          type="primary">
          See Detail
        </PdButton>
      </Col>
    </Row>
  );

  const columns = [
    {
      center: true,
      name: 'Inspection List',
      selector: row => row.damage_penalty.title,
      width: '25%',
    },
    {
      center: true,
      name: 'Description',
      selector: row => row.damage_penalty.description,
      width: '39%',
    },
    {
      center: true,
      format: (row) => convertNumberToRupiahCurrency(+row.price),
      name: 'Penalty Price',
      selector: row => row.damage_penalty,
      width: '20%',
    },
    {
      name: '',
      cell: buttonAction,
      width: '16%',
    },
  ];

  function handleOpenModal(inspection) {
    setModalContent(inspection);
    setShowModal(true);
  }

  function handleCloseModal() {
    setShowModal(false);
    setModalContent({});
  }

  function handleCloseCreateEdit() {
    fetchPenaltyDetail();
    handleCloseModal();
  }

  function handleBack() {
    navigate('/review_inspections');
  }

  async function fetchPenaltyDetail() {
    setLoadStatus(true);
    try {
      const {
        data: { data },
      } = await ReviewInspectionDetail.getReviewInspectionDetail(orderId);

      const {
        data: { data: { total_deposit, damage_penalty, late_penalty } = {} },
      } =
        await ReviewInspectionDetail.getOrderDetailOnReviewInspection(orderId);
      setTotalDepositAndDamage({
        total_deposit,
        damage_penalty,
        late_penalty,
      });
      setInspectionDetail(data);
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadStatus(false);
    }
  }

  async function handleSubmit() {
    setLoadingButton(true);
    try {
      await ReviewInspectionDetail.doneInspection(orderId);
      navigate('/review_inspections');
    } catch (error) {
      errHandling(error);
    } finally {
      setLoadingButton(false);
    }
  }

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (orderId) fetchPenaltyDetail();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);

  return (
    <Layout className="main-container pd-cms-penalty-detail">
      <div className="content">
        <div className="pageTitle">
          {pageStatus === INSPECTION_STATUS.CHECK && 'Check'} {pageTitle}
        </div>
        <Content className="pageContent section-container">
          {inspectionDetail.map(
            ({
              product: { title, price, id: product_id } = {},
              penalties = [],
              id,
            }) => (
              <div key={id} className="pd-margin-bottom-md inner-section">
                <Row gutter={[18, 36]} type="flex" align="middle">
                  <Col lg={12}>
                    <Title level={3}>{title}</Title>
                  </Col>
                  <Col lg={12}>
                    <Title level={3}>
                      {convertNumberToRupiahCurrency(+price)}
                    </Title>
                  </Col>
                </Row>

                <Row className="pd-margin-bottom-md">Inspection ID: {id}</Row>

                <Row>
                  {!loadStatus && (
                    <TableList
                      columns={columns}
                      loadingOrder={loadStatus}
                      orderList={penalties.map((pen) => ({
                        ...pen,
                        product_id,
                      }))}
                      responsive={false}
                    />
                  )}
                </Row>
                <div>
                  <span>
                    Total Damage:{' '}
                    {convertNumberToRupiahCurrency(
                      penalties.reduce(
                        (prev, { price } = {}) => prev + +price,
                        0
                      )
                    )}
                  </span>
                </div>
              </div>
            )
          )}

          {pageStatus === INSPECTION_STATUS.DONE && (
            <Row className="pd-margin-bottom-md inner-section">
              <Col span={12}>
                <Col className="pd-margin-bottom-xs">
                  <Row>
                    <Col span={12}>Total Deposit</Col>
                    <Col span={12}>
                      {convertNumberToRupiahCurrency(totalDeposit)}
                    </Col>
                  </Row>
                </Col>
                <Col className="pd-margin-bottom-xs">
                  <Row>
                    <Col span={12}>Total Damage</Col>
                    <Col span={12}>
                      {convertNumberToRupiahCurrency(damagePenalty)}
                    </Col>
                  </Row>
                </Col>
                <Col className="pd-margin-bottom-xs">
                  <Row>
                    <Col span={12}>Total Late Penalty</Col>
                    <Col span={12}>
                      {convertNumberToRupiahCurrency(latePenalty)}
                    </Col>
                  </Row>
                </Col>
                <hr />
                <Col>
                  <Row>
                    <Col span={12}>
                      <Title level={4}>Return to Customer:</Title>{' '}
                    </Col>
                    <Col span={12}>
                      <Title level={4}>
                        {convertNumberToRupiahCurrency(
                          returnToCustomerAmount >= 0
                            ? returnToCustomerAmount
                            : 0
                        )}
                      </Title>
                    </Col>
                  </Row>
                </Col>
              </Col>
            </Row>
          )}

          <Row
            className="pd-margin-top-xl"
            type="flex"
            justify="center"
            gutter={16}>
            <Col lg={8}>
              <PdButton
                className="height-32"
                type="danger"
                full
                onClick={handleBack}>
                Back
              </PdButton>
            </Col>
            <Col lg={8}>
              {pageStatus === INSPECTION_STATUS.CHECK && (
                <PdButton
                  className="height-32"
                  type="primary"
                  full
                  onClick={handleSubmit}>
                  Publish
                </PdButton>
              )}
            </Col>
          </Row>
        </Content>
      </div>
      {/* Modal Section */}
      {showModal && (
        <Modal
          open={showModal}
          wrapClassName="hidden-wrap-modal"
          centered
          onCancel={handleCloseModal}
          footer={false}>
          <InspectionCreateEdit
            closeModal={handleCloseCreateEdit}
            inspection={modalContent}
            warehouseList={[]}
          />
        </Modal>
      )}
    </Layout>
  );
}

export default { CheckingInspectionDetail };
